import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import React, { Fragment, useContext } from 'react';

import { PortalContext } from '../../context/PortalContext';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { sanitizeTestIdProps } from '../../utils/testid';
import { PortalIdentifier } from '../Portal';
import { PSXIcon } from '../PSXIcon/PSXIcon';
import { CircleBackground } from './CircleBackground';
import { IconContainer } from './IconContainer';
import { SquareBackground } from './SquareBackground';
import { Absolute } from './styles';
import {
  IIconBrandAndSymbolProps,
  IIconMappingNode,
  IOffsetOverrides,
  IconName,
} from './types';

const ICON_MAP: Record<IconName, IIconMappingNode> = {
  [IconName.clinicInCircle]: {
    iconBrandProps: { icon: 'clinic' },
    iconSymbolProps: { icon: 'business', backgroundShape: 'circle' },
  },
  [IconName.clipboardInSquare]: {
    iconBrandProps: { icon: 'clipboard' },
    iconSymbolProps: { icon: 'content_paste', backgroundShape: 'square' },
  },
  [IconName.costInSquare]: {
    iconBrandProps: { icon: 'cost' },
    iconSymbolProps: {
      icon: 'payments',
      backgroundShape: 'square',
      variant: 'outlined',
    },
  },
  [IconName.heartInCircle]: {
    iconBrandProps: { icon: 'heart' },
    iconSymbolProps: { icon: 'favorite', backgroundShape: 'circle' },
    iconSymbolOffsetOverrides: { top: 5 },
  },
  [IconName.lightBulbInSquare]: {
    iconBrandProps: { icon: 'light_bulb' },
    iconSymbolProps: {
      icon: 'lightbulb',
      backgroundShape: 'square',
      variant: 'outlined',
    },
  },
  [IconName.syringeInCircle]: {
    iconBrandProps: { icon: 'syringe' },
    iconSymbolProps: { icon: 'vaccines', backgroundShape: 'circle' },
  },
  [IconName.stethoscopeInCircle]: {
    iconBrandProps: { icon: 'stethoscope' },
    iconSymbolProps: {
      icon: 'stethoscope',
      backgroundShape: 'circle',
      variant: 'outlined',
    },
  },
  [IconName.giftInInvertedCircle]: {
    iconBrandProps: { icon: 'gift' },
    iconSymbolProps: {
      icon: 'trophy',
      backgroundShape: 'circle',
      color: '$core.color.brand.60',
      variant: 'filled',
    },
    backgroundColor: 'white',
  },
  [IconName.stethoscope]: {
    iconBrandProps: { icon: 'stethoscope' },
    iconSymbolProps: {
      icon: 'stethoscope',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.commonSearchStethoscope]: {
    iconBrandProps: { icon: 'stethoscope' },
    iconSymbolProps: {
      icon: 'stethoscope',
      variant: 'outlined',
    },
  },
  [IconName.quickCarePrimaryCare]: {
    iconBrandProps: { icon: 'stethoscope' },
    iconSymbolProps: {
      icon: 'stethoscope',
      variant: 'outlined',
    },
  },
  [IconName.brain]: {
    iconBrandProps: { icon: 'brain' },
    iconSymbolProps: {
      icon: 'psychology',
      variant: 'outlined',
    },
  },
  [IconName.beaker]: {
    iconBrandProps: { icon: 'beaker' },
    iconSymbolProps: { icon: 'science', variant: 'outlined' },
  },
  [IconName.tooth]: {
    iconBrandProps: { icon: 'tooth' },
    iconSymbolProps: { icon: 'dentistry', variant: 'outlined' },
  },
  [IconName.eye]: {
    iconBrandProps: { icon: 'eye' },
    iconSymbolProps: { icon: 'remove_red_eye', variant: 'outlined' },
  },
  [IconName.networkDarkCircle]: {
    iconBrandProps: { icon: 'network' },
    iconSymbolProps: { icon: 'groups', backgroundShape: 'circle' },
  },
  [IconName.magnifyingGlassDarkCircle]: {
    iconBrandProps: { icon: 'magnifying_glass', variant: 'twotone' },
    iconSymbolProps: { icon: 'search', backgroundShape: 'circle' },
  },
  [IconName.networkLightCircle]: {
    iconBrandProps: { icon: 'network', variant: 'twotonelightcircle' },
    iconSymbolProps: { icon: 'groups', backgroundShape: 'circle' },
  },
  [IconName.clinicLightCircle]: {
    iconBrandProps: { icon: 'clinic', variant: 'twotonelightcircle' },
    iconSymbolProps: { icon: 'business', backgroundShape: 'circle' },
  },
  [IconName.Organization]: {
    iconBrandProps: { icon: 'clinic' },
    iconSymbolProps: {
      icon: 'local_hospital',
      variant: 'outline',
      backgroundShape: 'circle',
    },
  },
  [IconName.Practitioner]: {
    iconBrandProps: { icon: 'stethoscope' },
    iconSymbolProps: {
      icon: 'stethoscope',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName['Medical Group']]: {
    iconBrandProps: { icon: 'network' },
    iconSymbolProps: {
      icon: 'groups',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName['Provider groups']]: {
    iconBrandProps: { icon: 'network' },
    iconSymbolProps: {
      icon: 'groups',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.mental_health]: {
    iconBrandProps: { icon: 'mental_health' },
    iconSymbolProps: {
      icon: 'psychology',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.network_circle]: {
    iconBrandProps: { icon: 'network_circle' },
    iconSymbolProps: {
      icon: 'groups',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.network]: {
    iconBrandProps: { icon: 'network' },
    iconSymbolProps: {
      icon: 'groups',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.clinic]: {
    iconBrandProps: { icon: 'clinic' },
    iconSymbolProps: {
      icon: 'local_hospital',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.UrgentCareClinic]: {
    iconBrandProps: { icon: 'clinic' },
    iconSymbolProps: {
      icon: 'local_hospital',
      variant: 'outlined',
    },
  },
  [IconName.care]: {
    iconBrandProps: { icon: 'care' },
    iconSymbolProps: {
      icon: 'volunteer_activism',
      variant: 'outlined',
    },
  },
  [IconName.crutches]: {
    iconBrandProps: { icon: 'crutches' },
    iconSymbolProps: {
      icon: 'masks',
      variant: 'outlined',
    },
  },
  [IconName.syringe]: {
    iconBrandProps: { icon: 'syringe' },
    iconSymbolProps: {
      icon: 'vaccines',
      variant: 'outlined',
    },
  },
  [IconName.tube]: {
    iconBrandProps: { icon: 'tube' },
    iconSymbolProps: {
      icon: 'healing',
      variant: 'outlined',
    },
  },
  [IconName.treadmill]: {
    iconBrandProps: { icon: 'treadmill' },
    iconSymbolProps: {
      icon: 'personal_injury',
      variant: 'outlined',
    },
  },
  [IconName.chiropractor]: {
    iconBrandProps: { icon: 'chiropractor' },
    iconSymbolProps: {
      icon: 'airline_seat_flat',
      variant: 'outlined',
    },
  },
  [IconName.commonSearchMentalHealth]: {
    iconBrandProps: { icon: 'mental_health' },
    iconSymbolProps: {
      icon: 'psychology',
      variant: 'outlined',
    },
  },
  [IconName.suicideAndCrisisLifelineIcon]: {
    iconBrandProps: { icon: 'mental_health', variant: 'twotone' },
    iconSymbolProps: {
      icon: 'person',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.substanceUseIcon]: {
    iconBrandProps: { icon: 'care', variant: 'twotone' },
    iconSymbolProps: {
      icon: 'volunteer_activism',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.emergencyServicesIcon]: {
    iconBrandProps: { icon: 'ambulance', variant: 'twotone' },
    iconSymbolProps: {
      icon: 'medical_services',
      variant: 'outlined',
      backgroundShape: 'circle',
    },
  },
  [IconName.tablet]: {
    iconBrandProps: { icon: 'tablet', variant: 'twotone' },
    iconSymbolProps: {
      icon: 'tablet',
      variant: 'outlined',
      backgroundShape: 'circle',
      size: 84,
    },
    iconSymbolOffsetOverrides: { top: 8, left: 9 },
  },
  [IconName.phone]: {
    iconBrandProps: { icon: 'phone' },
    iconSymbolProps: {
      icon: 'phone_in_talk',
      variant: 'outlined',
    },
  },
};

const calculateOffsets = (
  standardOffset: number,
  offsetOverrides?: IOffsetOverrides
) => ({
  top: standardOffset,
  left: standardOffset,
  ...offsetOverrides,
});

export const IconBrandAndSymbol = ({
  icon,
  size: backgroundIconSize,
  children,
  isCareCard,
  isCobrandedBenefitCard,
  ...otherProps
}: IIconBrandAndSymbolProps) => {
  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;
  const isUsingBrandIcons = !isGehaTheme || !ICON_MAP[icon];

  // test id helpers
  sanitizeTestIdProps(otherProps);
  const { 'data-auto-testid': dataAutoTestId, 'data-testid': dataTestId } =
    otherProps;
  const dataAutoTestIdPrefix = dataAutoTestId ? `${dataAutoTestId}-` : '';
  const dataTestIdPrefix = dataTestId ? `${dataTestId}-` : '';

  // icon and background shape helpers
  const iconNode = ICON_MAP[icon];
  const propsFromIconMap =
    iconNode?.[isUsingBrandIcons ? 'iconBrandProps' : 'iconSymbolProps'];
  const hasCircleBackground =
    !isUsingBrandIcons && propsFromIconMap?.backgroundShape === 'circle';
  const hasSquareBackground =
    !isUsingBrandIcons && propsFromIconMap?.backgroundShape === 'square';
  const hasBackground = hasCircleBackground || hasSquareBackground;

  // helpers for icon size and offset calculations
  const r = backgroundIconSize / 2;
  const innerIconSize = hasBackground
    ? Math.sqrt(2 * (r * r))
    : backgroundIconSize;
  const standardOffset = hasBackground
    ? (backgroundIconSize - innerIconSize) / 2
    : 0;
  const offsetOverrides =
    !isUsingBrandIcons && iconNode ? iconNode.iconSymbolOffsetOverrides : {};
  const offsets = calculateOffsets(standardOffset, offsetOverrides);

  // icon and background color helpers
  const backgroundFillColor =
    isCareCard && isGEHAV2
      ? 'transparent'
      : iconNode?.backgroundColor ?? '$core.color.brand.60';
  const color = hasBackground ? 'white' : '$core.color.brand.100';

  const IconComponent = () => {
    if (children && isUsingBrandIcons) {
      return <Fragment>{children}</Fragment>;
    }
    const HelperIcon = isUsingBrandIcons ? IconBrand : IconSymbol;

    if (isCobrandedBenefitCard) {
      return (
        <PSXIcon
          color={color}
          data-auto-testid={`${dataAutoTestIdPrefix}icon-component`}
          data-testid={`${dataTestIdPrefix}icon-component`}
          icon={icon}
          size={innerIconSize}
          {...otherProps}
          {...propsFromIconMap}
        />
      );
    }

    return (
      <HelperIcon
        color={color} // this will be overridden by any value in props
        data-auto-testid={`${dataAutoTestIdPrefix}icon-component`}
        data-testid={`${dataTestIdPrefix}icon-component`}
        icon={icon} // this will be overridden by any value in props from map
        size={innerIconSize}
        {...otherProps}
        {...propsFromIconMap}
      />
    );
  };

  if (!iconNode && !isUsingBrandIcons) {
    return null;
  }
  return (
    <IconContainer
      data-auto-testid={`${dataAutoTestIdPrefix}icon-container`}
      data-testid={`${dataTestIdPrefix}icon-container`}
      size={backgroundIconSize}
    >
      <Absolute>
        {!isUsingBrandIcons &&
          propsFromIconMap.backgroundShape === 'circle' && (
            <CircleBackground
              data-auto-testid={`${dataAutoTestIdPrefix}circle-background`}
              data-testid={`${dataTestIdPrefix}circle-background`}
              fill={backgroundFillColor}
              radius={backgroundIconSize / 2}
            />
          )}
        {!isUsingBrandIcons &&
          propsFromIconMap.backgroundShape === 'square' && (
            <SquareBackground
              borderRadius={6}
              data-auto-testid={`${dataAutoTestIdPrefix}square-background`}
              data-testid={`${dataTestIdPrefix}square-background`}
              fill={backgroundFillColor}
              size={backgroundIconSize}
            />
          )}
      </Absolute>
      <Absolute css={{ ...offsets }}>
        <IconComponent />
      </Absolute>
    </IconContainer>
  );
};
